import { useQuery } from '@tanstack/react-query';
import type { LineString } from 'geojson';
import { BellRing, Bus, MessageSquareWarning, Route, Smartphone } from 'lucide-react';
import { type ComponentType, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { HeatmapLegend, HourBars, LineAlerts, TopStations, versus } from '@/components/dashboard-widgets';
import { HeatmapLayers } from '@/components/map/heatmap-layers';
import { arrowIcon } from '@/components/map/icons';
import { arrowLayer, fc, GeoJsonLayer, lineLayer } from '@/components/map/layers';
import { type LineStop, LineStopsLayer } from '@/components/map/line-stops-layer';
import { LiveBusesLayer } from '@/components/map/live-buses';
import { boundsOf, MapView, useMap } from '@/components/map/map-view';
import { StationsLayer } from '@/components/map/stations-layer';
import { BusPanel, StationPanel, useBusInfo } from '@/components/map-panels';
import { Select } from '@/components/ui/form';
import { Card, PageHeader } from '@/components/ui/layout';
import { useAlertCount } from '@/lib/alerts';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { safeHex } from '@/lib/colors';
import { timeAgo } from '@/lib/format';
import { locale, t, tn } from '@/lib/i18n';
import { useLiveBuses } from '@/lib/live-buses';
import { linesCollection, type Network, useNetwork } from '@/lib/network';
import { type DayPart, type HeatmapKind, useHeatmap, useLineChecks, useOverview } from '@/lib/stats';
import type { AuditEntry } from '@/lib/types';
import { travelOrdered } from '@/lib/zone-check';

/** Audit entity names as the object of an activity sentence ("created the bus"). */
const ENTITIES: Record<string, string> = {
  advertisement: t('the station ad'),
  bus: t('the bus'),
  campaign: t('the campaign'),
  file: t('a file'),
  interest_point: t('the point of interest'),
  line: t('the direction'),
  news: t('the news article'),
  notification: t('the notification'),
  out_of_service_area: t('the out-of-service area'),
  pin_advertisement: t('the map pin'),
  report: t('the report'),
  schedule: t('the timetable'),
  station: t('the station'),
  user: t('the user'),
  zone: t('the line'),
};

/** What the user did, e.g. "created the bus"; unknown actions are shown as they come. */
function activityText(action: string, entity: string): string {
  const what = ENTITIES[entity] ?? entity.replace(/_/g, ' ');
  switch (action) {
    case 'create':
      return t('created {entity}', { entity: what });
    case 'update':
      return t('updated {entity}', { entity: what });
    case 'delete':
      return t('deleted {entity}', { entity: what });
    case 'archive':
      return t('archived {entity}', { entity: what });
    case 'restore':
      return t('restored {entity}', { entity: what });
    case 'reply':
      return t('replied to {entity}', { entity: what });
    case 'upload':
      return t('uploaded {entity}', { entity: what });
    case 'assign_zone':
      return t('assigned a line to {entity}', { entity: what });
    case 'login':
      return t('signed in');
    case 'change_password':
      return t('changed their password');
    default:
      return `${action.replace(/_/g, ' ')} ${entity.replace(/_/g, ' ')}`;
  }
}

interface Dashboard {
  buses: { active: number; total: number };
  zones_active: number;
  stations: number;
  reports: { open: number; unreplied: number };
  news: number;
  recent_activity: AuditEntry[];
}
function Stat({
  icon: Icon,
  label,
  value,
  sub,
  to,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  sub?: string;
  /** Page the card opens; without it the card is not a link. */
  to?: string;
}) {
  const body = (
    <>
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Icon className="size-4" /> {label}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </>
  );
  return to ? (
    <Link
      to={to}
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:border-brand-500"
    >
      {body}
    </Link>
  ) : (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">{body}</div>
  );
}

export function DashboardPage() {
  const { can } = useAuth();
  const data = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<Dashboard>('/dashboard'),
    refetchInterval: 30_000,
  });
  const network = useNetwork();
  const { buses, updatedAt } = useLiveBuses();
  // The selected bus or station lives in the URL (?bus=… / ?station=…), so going back from an edit
  // page shows the same selection again. Replaced, not pushed: selecting adds no history entries.
  const [params, setParams] = useSearchParams();
  const selected: Selection | null = params.get('bus')
    ? { kind: 'bus', id: params.get('bus')! }
    : params.get('station')
      ? { kind: 'station', id: params.get('station')! }
      : null;
  const setSelected = (next: Selection | null) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('bus');
        p.delete('station');
        if (next) p.set(next.kind, next.id);
        return p;
      },
      { replace: true },
    );
  const busInfo = useBusInfo(selected?.kind === 'bus' ? selected.id : null);
  const focusLine = selected?.kind === 'bus' ? busInfo.data?.bus.line?._id : undefined;
  const focusColor = safeHex(busInfo.data?.bus.zone?.color ?? busInfo.data?.bus.color, '#0b3f82');
  const focus = useMemo(
    () => busLineView(network.data, focusLine, focusColor),
    [network.data, focusLine, focusColor],
  );
  // A bus on the wrong line (or without one): also the line it actually drives, dashed.
  const check = selected?.kind === 'bus' ? busInfo.data?.bus.line_check : undefined;
  const driven =
    check && (check.status === 'wrong_zone' || check.status === 'unassigned') ? check.suggested_zone : null;
  const drivenView = useMemo(() => drivenLinesView(network.data, driven?._id), [network.data, driven?._id]);
  const dim = focus || drivenView ? 0.25 : 1;
  const overview = useOverview();
  const o = overview.data;
  const checks = useLineChecks();
  const flagged = checks.data?.items ?? [];
  // Map badges only for the findings with a fix (off route is listed below the map).
  const warnings = useMemo(
    () => new Set(flagged.filter((b) => b.line_check.suggested_zone).map((b) => b._id)),
    [flagged],
  );
  const [heat, setHeat] = useState<HeatmapKind | null>(null);
  const [heatDays, setHeatDays] = useState(7);
  const [heatPart, setHeatPart] = useState<DayPart>('all');
  const heatmap = useHeatmap(heat, heatDays, heatPart);
  const mapRef = useRef<HTMLDivElement>(null);
  const scrollToMap = () => mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // Back from an edit page with a selection in the URL: show the map with it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only on the first render
  useEffect(() => {
    if (selected) mapRef.current?.scrollIntoView({ block: 'start' });
  }, []);
  const showBus = (id: string) => {
    setSelected({ kind: 'bus', id });
    scrollToMap();
  };
  const showStation = (id: string) => {
    setSelected({ kind: 'station', id });
    scrollToMap();
  };
  const visible = buses.filter((b) => b.is_visible && b.geometry);
  // Buses drawn on the map: the visible ones, plus the selected bus even when it is hidden or not in
  // the live feed (no zone), e.g. when picked from "Buses to check".
  const selectedBusId = selected?.kind === 'bus' ? selected.id : null;
  const mapBuses = useMemo(() => {
    const shown = buses.filter((b) => b.is_visible || b._id === selectedBusId);
    const info = busInfo.data?.bus;
    if (
      selectedBusId &&
      info?._id === selectedBusId &&
      info.geometry &&
      !shown.some((b) => b._id === selectedBusId)
    )
      shown.push({
        _id: info._id,
        name: info.name,
        number: info.number,
        color: info.color,
        heading: info.heading,
        is_active: info.is_active,
        is_visible: info.is_visible,
        geometry: info.geometry,
      });
    return shown;
  }, [buses, selectedBusId, busInfo.data]);
  const selectedPos = mapBuses.find((b) => b._id === selectedBusId)?.geometry?.coordinates;
  const inService = visible.filter((b) => b.is_active).length;
  const d = data.data;
  return (
    <>
      <PageHeader title={t('Dashboard')} subtitle={t('Live overview of the network')} />
      <OpenAlertsBanner />
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          icon={Bus}
          label={t('Buses on route')}
          value={d ? d.buses.active : '—'}
          sub={d ? tn(d.buses.total, 'of {n} vehicle', 'of {n} vehicles') : undefined}
          to="/buses"
        />
        <Stat
          icon={Route}
          label={t('Km driven today')}
          value={o ? o.km.today.toLocaleString(locale) : '—'}
          sub={o ? versus(o.km.today, o.km.yesterday_same_time) : undefined}
          to="/buses"
        />
        <Stat
          icon={Smartphone}
          label={t('App requests today')}
          value={o ? o.requests.today.toLocaleString(locale) : '—'}
          sub={o ? versus(o.requests.today, o.requests.yesterday_same_time) : undefined}
          to={can('admin') ? '/demand' : undefined}
        />
        <Stat
          icon={MessageSquareWarning}
          label={t('Reports to answer')}
          value={d?.reports.unreplied ?? '—'}
          sub={d ? t('{n} open', { n: d.reports.open }) : undefined}
          to="/reports"
        />
      </div>
      <div className="space-y-6">
        <div ref={mapRef} className="scroll-mt-4">
          <Card
            title={t('Live map')}
            actions={
              <div className="flex flex-wrap items-center justify-end gap-2">
                <span className="mr-2 text-xs text-slate-500">
                  {updatedAt
                    ? `${tn(visible.length, '{n} bus', '{n} buses')} · ${t('{n} in service', { n: inService })} · ${t('updated {time}', { time: updatedAt.toLocaleTimeString(locale) })}`
                    : t('Connecting…')}
                </span>
                <Select
                  className="w-56"
                  aria-label={t('Map layer')}
                  value={heat ?? ''}
                  onChange={(e) => setHeat((e.target.value || null) as HeatmapKind | null)}
                >
                  <option value="">{t('Live buses')}</option>
                  <option value="bus">{t('Heatmap: bus speed')}</option>
                  <option value="user">{t('Heatmap: passengers')}</option>
                </Select>
                {heat && (
                  <>
                    <Select
                      className="w-36"
                      aria-label={t('Period')}
                      value={heatDays}
                      onChange={(e) => setHeatDays(Number(e.target.value))}
                    >
                      <option value={1}>{t('Today')}</option>
                      <option value={7}>{t('Last {n} days', { n: 7 })}</option>
                      <option value={30}>{t('Last {n} days', { n: 30 })}</option>
                      <option value={90}>{t('Last {n} days', { n: 90 })}</option>
                    </Select>
                    <Select
                      className="w-44"
                      aria-label={t('Time of day')}
                      value={heatPart}
                      onChange={(e) => setHeatPart(e.target.value as DayPart)}
                    >
                      <option value="all">{t('All day')}</option>
                      <option value="morning">{t('Morning (6–10)')}</option>
                      <option value="midday">{t('Midday (10–15)')}</option>
                      <option value="afternoon">{t('Afternoon (15–19)')}</option>
                      <option value="evening">{t('Evening (19–24)')}</option>
                      <option value="night">{t('Night (0–6)')}</option>
                    </Select>
                  </>
                )}
              </div>
            }
            padded={false}
          >
            <MapView className="h-[calc(100vh-20rem)] min-h-[480px] rounded-none border-0">
              <GeoJsonLayer
                data={linesCollection(network.data?.lines)}
                layers={[lineLayer(undefined, 3, 0.45)]}
                dim={dim}
              />
              <HeatmapLayers kind={heat} cells={heatmap.data?.cells ?? []} />
              <StationsLayer
                dim={heat ? Math.min(dim, 0.5) : dim}
                stations={network.data?.stations ?? []}
                selected={selected?.kind === 'station' ? selected.id : null}
                onSelect={(id) => setSelected({ kind: 'station', id })}
              />
              {/* The line a wrongly assigned bus drives (dashed), under its own line. */}
              <GeoJsonLayer data={drivenView ?? EMPTY} layers={[DASHED]} />
              {/* The selected bus's line: always mounted so it stays between the stations and the buses. */}
              <GeoJsonLayer
                data={focus?.route ?? EMPTY}
                layers={[lineLayer(['get', 'color'], 5, 0.9), arrowLayer(['get', 'arrow'] as never)]}
              />
              <LineStopsLayer
                stops={focus?.stops ?? []}
                color={focusColor}
                onClick={(p) => setSelected({ kind: 'station', id: String(p.id) })}
              />
              <FitOnChange
                id={
                  selectedBusId && (focus || selectedPos)
                    ? `${selectedBusId}|${focusLine}|${driven?._id ?? ''}`
                    : null
                }
                bounds={boundsOf(
                  focus?.stops.map((x) => x.position),
                  focus?.route.features.map((f) => (f.geometry as LineString).coordinates),
                  drivenView?.features.map((f) => (f.geometry as LineString).coordinates),
                  selectedPos,
                )}
              />
              <LiveBusesLayer
                fadeOthers
                warnings={warnings}
                buses={mapBuses}
                showHidden
                highlight={selected?.kind === 'bus' ? selected.id : null}
                onSelect={(id) => setSelected({ kind: 'bus', id })}
              />
              {selected?.kind === 'bus' && (
                <BusPanel
                  id={selected.id}
                  onClose={() => setSelected(null)}
                  onSelectStation={(id) => setSelected({ kind: 'station', id })}
                />
              )}
              {selected?.kind === 'station' && (
                <StationPanel
                  id={selected.id}
                  onClose={() => setSelected(null)}
                  onSelectBus={(id) => setSelected({ kind: 'bus', id })}
                />
              )}
              {heat && <HeatmapLegend kind={heat} />}
              {!heat && drivenView && driven && (
                <DrivenLegend own={busInfo.data?.bus.zone ?? null} ownColor={focusColor} driven={driven} />
              )}
            </MapView>
          </Card>
        </div>
        {flagged.length > 0 && (
          <Card title={t('Buses to check ({n})', { n: flagged.length })} padded={false}>
            <LineAlerts items={flagged} selected={selectedBusId} onSelect={showBus} />
          </Card>
        )}
        <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-4">
          <Card title={t('Buses on route, by hour')} className="xl:col-span-1">
            {o ? (
              <HourBars
                today={o.buses_by_hour.today}
                yesterday={o.buses_by_hour.yesterday}
                hourNow={o.hour}
              />
            ) : (
              <p className="text-sm text-slate-500">…</p>
            )}
          </Card>
          <Card title={t('App requests, by hour')} className="xl:col-span-1">
            {o ? (
              <HourBars
                today={o.requests.by_hour.today}
                yesterday={o.requests.by_hour.yesterday}
                hourNow={o.hour}
              />
            ) : (
              <p className="text-sm text-slate-500">…</p>
            )}
          </Card>
          <Card
            title={t('Most asked stations (7 days)')}
            actions={
              can('admin') && (
                <Link to="/demand" className="text-sm font-medium text-brand-700 hover:underline">
                  {t('See all')} →
                </Link>
              )
            }
          >
            <TopStations items={o?.top_stations ?? []} onSelect={showStation} />
          </Card>
          {can('admin') && (
            <Card title={t('Recent activity')}>
              {d?.recent_activity.length ? (
                <ul className="max-h-64 space-y-3 overflow-y-auto text-sm">
                  {d.recent_activity.map((a) => (
                    <li key={a._id}>
                      <div>
                        <span className="font-medium">{a.user_email ?? t('system')}</span>{' '}
                        {activityText(a.action, a.entity)}
                        {a.summary && <span className="text-slate-600"> “{a.summary}”</span>}
                      </div>
                      <div className="text-xs text-slate-400">{timeAgo(a.at)}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500">{t('Nothing in the last {n} hours', { n: 24 })}</p>
              )}
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

const EMPTY = fc([]);
type Selection = { kind: 'bus' | 'station'; id: string };
const DASHED_BASE = lineLayer(['get', 'color'], 4, 0.9);
const DASHED = {
  ...DASHED_BASE,
  paint: { ...DASHED_BASE.paint, 'line-dasharray': [1.5, 1.5] },
} as typeof DASHED_BASE;

/** Both lines (going and return) of the zone a bus actually drives, in that zone's colour. */
function drivenLinesView(network: Network | undefined, zoneId: string | undefined) {
  const zone = zoneId ? network?.zones.find((z) => z._id === zoneId) : undefined;
  if (!network || !zone) return null;
  const color = safeHex(zone.color);
  const lines = [zone.on_line_going, zone.on_line_return]
    .map((id) => network.lines.find((l) => l._id === id))
    .filter((l) => !!l);
  if (!lines.length) return null;
  return fc(lines.map((l) => ({ type: 'Feature', geometry: l.geometry, properties: { color } })));
}

/** Map key for a bus on the wrong line: its own line (solid) and the one it drives (dashed). */
function DrivenLegend({
  own,
  ownColor,
  driven,
}: {
  own: { bus_sign: string; name: string } | null;
  ownColor: string;
  driven: { bus_sign: string; name: string; color: string };
}) {
  return (
    <div className="absolute right-2 bottom-2 z-10 space-y-1 rounded-md bg-white/95 px-2.5 py-2 text-[11px] text-slate-600 shadow ring-1 ring-slate-200">
      {own && (
        <div className="flex items-center gap-2">
          <span className="h-1 w-6 rounded" style={{ background: ownColor }} />
          {t('Assigned line')}:{' '}
          <span className="font-medium text-slate-800">
            {own.bus_sign} · {own.name}
          </span>
        </div>
      )}
      <div className="flex items-center gap-2">
        <span
          className="h-1 w-6"
          style={{
            background: `repeating-linear-gradient(90deg, ${safeHex(driven.color)} 0 5px, transparent 5px 9px)`,
          }}
        />
        {t('Drives')}:{' '}
        <span className="font-medium text-slate-800">
          {driven.bus_sign} · {driven.name}
        </span>
      </div>
    </div>
  );
}

/** A line in travel order with its stops, for highlighting the selected bus's route. */
function busLineView(network: Network | undefined, lineId: string | undefined, color: string) {
  const line = lineId ? network?.lines.find((l) => l._id === lineId) : undefined;
  if (!network || !line) return null;
  const stations = new Map(network.stations.map((s) => [s._id, s]));
  const stops: LineStop[] = line.ordered_stations.flatMap((id) => {
    const s = stations.get(id);
    return s ? [{ id, name: s.name, position: s.geometry.coordinates as [number, number] }] : [];
  });
  return {
    route: fc([
      {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: travelOrdered(line, stations) },
        properties: { color, arrow: arrowIcon(color) },
      },
    ]),
    stops,
  };
}

/** Fits the map to `bounds` once per `id` (leaving room for the card on the left). */
function FitOnChange({ id, bounds }: { id: string | null; bounds: ReturnType<typeof boundsOf> }) {
  const map = useMap();
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when the selection changes
  useEffect(() => {
    if (!map || !id || !bounds) return;
    // Room for the card on the left, unless the map is narrow.
    const left = Math.min(360, map.getContainer().clientWidth / 2);
    map.fitBounds(bounds, {
      padding: { top: 40, bottom: 40, right: 40, left },
      maxZoom: 15,
      duration: 600,
    });
  }, [map, id]);
  return null;
}

function OpenAlertsBanner() {
  const open = useAlertCount().data?.open ?? 0;
  if (!open) return null;
  return (
    <Link
      to="/alerts"
      className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800 hover:bg-red-100"
    >
      <BellRing className="size-4" />
      {tn(open, '{n} open alert', '{n} open alerts')}
      <span className="ml-auto font-medium">{t('See alerts')} →</span>
    </Link>
  );
}
